import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { format, formatDistanceToNow, parseISO, isValid } from 'date-fns';
import { es as dateFnsEs, ptBR as dateFnsPtBR, fr as dateFnsFr } from 'date-fns/locale';
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  Pencil,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useUser } from '@/contexts/UserContext';
import {
  countMissingSalesMapRows,
  deleteSalesMapOverride,
  fetchAccountSalesMap,
  fetchAccountSalesMapNbm,
  mergeNbmIntoSalesMapCache,
  getSalesMapTableRows,
  isMissingStrengthRow,
  isSalesMapCacheFresh,
  patchSalesMapOverride,
  peekSalesMapCache,
  resolveNextBestMove,
  resolveStrengthLabel,
  sanitizeNextBestMoveForDisplay,
} from '@/lib/salesMapApi';
import { trackSalesMapAction } from '@/components/utils/analytics';
import { useTranslation } from '@/components/utils/translations';

/** date-fns locale for the UI language; undefined (= date-fns default, English) for en. */
const DATE_FNS_LOCALES = { es: dateFnsEs, 'pt-BR': dateFnsPtBR, fr: dateFnsFr };

/** Translation keys for the MEDDPICC subtitle, keyed on the stable sales_point. */
const MEDDPICC_DIMENSION = {
  pain_need: 'acctMeddpiccIdentifyPain',
  impact_scale: 'acctMeddpiccMetrics',
  why_now: 'acctMeddpiccPainTriggerTiming',
  current_vendor: 'acctMeddpiccCompetitionIncumbent',
  buyer_owner: 'acctMeddpiccEconomicBuyer',
  decision_process: 'acctMeddpiccDecisionProcess',
  procurement_paper_process: 'acctMeddpiccPaperProcess',
  champion: 'acctMeddpiccChampion',
};

const MEDDPICC_DIMENSION_TOOLTIP = {
  pain_need: 'acctMeddpiccTipPainNeed',
  impact_scale: 'acctMeddpiccTipImpactScale',
  why_now: 'acctMeddpiccTipWhyNow',
  current_vendor: 'acctMeddpiccTipCurrentVendor',
  buyer_owner: 'acctMeddpiccTipBuyerOwner',
  decision_process: 'acctMeddpiccTipDecisionProcess',
  procurement_paper_process: 'acctMeddpiccTipPaperProcess',
  champion: 'acctMeddpiccTipChampion',
};

/**
 * Display-only translation of the API row label, keyed on the stable sales_point.
 * The second entry is the English label the API sends today (SALES_POINT_LABELS in
 * neo-rag salesMap.ts); the key is used only when the API label still matches it,
 * so a renamed or tenant-specific label from the API is shown as-is.
 */
const SALES_POINT_LABEL_KEYS = {
  pain_need: ['acctSalesPointPainNeed', 'Pain / Need'],
  impact_scale: ['acctSalesPointImpactScale', 'Impact / Scale'],
  why_now: ['acctSalesPointWhyNow', 'Why Now / Timing'],
  current_vendor: ['acctSalesPointCurrentVendor', 'Current Vendor / Competition'],
  buyer_owner: ['acctSalesPointBuyerOwner', 'Buyer / Owner'],
  decision_process: ['acctSalesPointDecisionProcess', 'Decision Process'],
  procurement_paper_process: ['acctSalesPointPaperProcess', 'Procurement / Paper Process'],
  champion: ['acctSalesPointChampion', 'Champion / Key Persona'],
  recommended_next_move: ['acctSalesPointRecommendedNextMove', 'Recommended Next Move'],
};

function salesPointDisplayLabel(row, t) {
  const known = SALES_POINT_LABEL_KEYS[row?.sales_point];
  if (known && row?.label === known[1]) return t(known[0]);
  return row?.label;
}

const STRENGTH_STYLES = {
  Missing: 'bg-gray-50 text-gray-500 border-gray-200',
  'Very Low': 'bg-rose-50 text-rose-700 border-rose-200',
  Low: 'bg-orange-50 text-orange-700 border-orange-200',
  Medium: 'bg-amber-50 text-amber-800 border-amber-200',
  High: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Very High': 'bg-emerald-100 text-emerald-800 border-emerald-300',
};

const STRENGTH_TOOLTIPS = {
  Missing: 'acctStrengthTipMissing',
  'Very Low': 'acctStrengthTipVeryLow',
  Low: 'acctStrengthTipLow',
  Medium: 'acctStrengthTipMedium',
  High: 'acctStrengthTipHigh',
  'Very High': 'acctStrengthTipVeryHigh',
};

/** Display-only translation keys for the strength label (the label itself stays the logic value). */
const STRENGTH_LABEL_KEYS = {
  Missing: 'acctStrengthMissing',
  'Very Low': 'acctStrengthVeryLow',
  Low: 'low',
  Medium: 'medium',
  High: 'high',
  'Very High': 'acctStrengthVeryHigh',
};

const SCORE_OVERRIDE_OPTIONS = [
  { value: 'keep', label: 'Keep derived' },
  { value: '0', label: '0 — Missing', strength: 'Missing' },
  { value: '1', label: '1 — Very Low', strength: 'Very Low' },
  { value: '2', label: '2 — Low', strength: 'Low' },
  { value: '3', label: '3 — Medium', strength: 'Medium' },
  { value: '4', label: '4 — High', strength: 'High' },
  { value: '5', label: '5 — Very High', strength: 'Very High' },
];

function formatRelativeTime(iso, locale) {
  if (!iso) return '';
  try {
    const d = parseISO(iso);
    if (!isValid(d)) return '';
    return formatDistanceToNow(d, { addSuffix: true, locale });
  } catch {
    return '';
  }
}

function EditedBadge() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
      <Pencil className="w-2.5 h-2.5" />
      {t('acctEdited')}
    </span>
  );
}

const COMPETITOR_AUTO_EDITOR_ID = 'system-competitor-auto';

function isCompetitorAutoOverride(repOverride) {
  return repOverride?.edited_by_user_id === COMPETITOR_AUTO_EDITOR_ID;
}

function AutoDetectedPill() {
  const { t } = useTranslation();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0 items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-600 cursor-help">
          🤖 {t('acctAutoDetected')}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs bg-[#0A1A2F] text-white border-0 text-xs p-3">
        {t('acctAutoDetectedTooltip')}
      </TooltipContent>
    </Tooltip>
  );
}

function formatGeneratedAt(iso, locale) {
  if (!iso) return '';
  try {
    const d = parseISO(iso);
    if (!isValid(d)) return '';
    return format(d, 'd MMM yyyy HH:mm', { locale });
  } catch {
    return '';
  }
}

function StrengthBadge({ label }) {
  const { t } = useTranslation();
  const resolved = STRENGTH_STYLES[label] ? label : 'Missing';
  const cls = STRENGTH_STYLES[resolved];
  const tooltip = t(STRENGTH_TOOLTIPS[resolved]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`inline-flex shrink-0 items-center rounded border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${cls}`}
        >
          {t(STRENGTH_LABEL_KEYS[resolved])}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs max-w-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

function MeddpiccSubtitle({ salesPoint }) {
  const { t } = useTranslation();
  const dimensionKey = MEDDPICC_DIMENSION[salesPoint];
  if (!dimensionKey) return null;

  const dimension = t(dimensionKey);
  const tooltipKey = MEDDPICC_DIMENSION_TOOLTIP[salesPoint];
  const tooltip = tooltipKey ? t(tooltipKey) : null;
  const subtitle = (
    <p className="text-[11px] uppercase tracking-wide text-[#7A8694] mt-0.5">
      MEDDPICC · {dimension}
    </p>
  );

  if (!tooltip) return subtitle;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <p className="text-[11px] uppercase tracking-wide text-[#7A8694] mt-0.5 cursor-default">
          MEDDPICC · {dimension}
        </p>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs max-w-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

function TruncatedView({ text }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef(null);
  const value = String(text || '').trim() || '—';

  useLayoutEffect(() => {
    if (expanded) return;
    const el = contentRef.current;
    if (!el) return;
    setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [value, expanded]);

  return (
    <div className="min-w-0">
      <p
        ref={contentRef}
        className={`text-sm text-[#0A1A2F] break-words leading-relaxed ${
          expanded ? '' : 'line-clamp-4'
        }`}
      >
        {value}
      </p>
      {isOverflowing ? (
        <button
          type="button"
          className="mt-1 text-[#00C2C2] text-xs font-medium hover:underline"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? t('acctShowLessLower') : t('acctShowMoreLower')}
        </button>
      ) : null}
    </div>
  );
}

function SourceRefs({ refs, row }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const items = Array.isArray(refs) ? refs : [];

  if (isMissingStrengthRow(row) || items.length === 0) {
    return null;
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-2">
      <CollapsibleTrigger className="flex items-center gap-1 text-xs text-[#5C6B7A] hover:text-[#0A1A2F]">
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {t('acctSourcesCount', { n: items.length })}
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-1 space-y-1 pl-4 border-l border-gray-100">
        {items.map((ref, idx) => {
          const label = `[${ref.table || 'source'}] ${ref.summary || ''}`.trim();
          if (ref.url) {
            return (
              <a
                key={`${ref.row_id || idx}-${ref.table}`}
                href={ref.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-1 text-xs text-[#00C2C2] hover:underline break-words"
              >
                <span className="flex-1">{label}</span>
                <ExternalLink className="w-3 h-3 shrink-0 mt-0.5" />
              </a>
            );
          }
          return (
            <p key={`${ref.row_id || idx}-${ref.table}`} className="text-xs text-[#5C6B7A] break-words">
              {label}
            </p>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
}

function RepOverrideNote({ repOverride, onEdit, onClear, clearing }) {
  const { t, language } = useTranslation();
  if (!repOverride?.rationale) return null;

  const author = repOverride.edited_by_user_name || t('acctRepLower');
  const when = formatRelativeTime(repOverride.updated_at, DATE_FNS_LOCALES[language]);

  return (
    <div className="mt-3 border-l-4 border-l-blue-500 bg-blue-50/40 p-3 rounded-r-md">
      <p className="text-xs font-medium text-blue-900">
        📝 {t('acctTeamNoteFrom', { author })}
        {when ? `, ${when}` : ''}
      </p>
      <p className="mt-1.5 text-sm text-[#0A1A2F] whitespace-pre-wrap break-words leading-relaxed">
        {repOverride.rationale}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          disabled={clearing}
          onClick={onClear}
        >
          {clearing ? <Loader2 className="w-3 h-3 animate-spin" /> : t('acctClearOverride')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 text-xs"
          disabled={clearing}
          onClick={onEdit}
        >
          {t('edit')}
        </Button>
      </div>
    </div>
  );
}

function SalesMapRowEditor({
  row,
  rationale,
  scoreChoice,
  validationError,
  saving,
  clearing,
  onRationaleChange,
  onScoreChange,
  onSave,
  onCancel,
  onClearOverride,
}) {
  const { t } = useTranslation();
  const hasOverride = Boolean(row.rep_override);

  return (
    <div className="mt-3 rounded-lg border border-[#00C2C2]/30 bg-gray-50/80 p-3 space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor={`rationale-${row.sales_point}`} className="text-xs font-medium text-[#0A1A2F]">
          {t('acctRationale')} <span className="text-red-500">*</span>
        </Label>
        <Textarea
          id={`rationale-${row.sales_point}`}
          rows={3}
          value={rationale}
          onChange={(e) => onRationaleChange(e.target.value)}
          placeholder={t('acctRationalePlaceholder')}
          className="text-sm resize-y min-h-[72px]"
        />
        {validationError ? (
          <p className="text-xs text-red-600">{validationError}</p>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`score-${row.sales_point}`} className="text-xs font-medium text-[#0A1A2F]">
          {t('acctOverrideScoreOptional')}
        </Label>
        <Select value={scoreChoice} onValueChange={onScoreChange}>
          <SelectTrigger id={`score-${row.sales_point}`} className="h-9 text-sm bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SCORE_OVERRIDE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.strength
                  ? `${opt.value} — ${t(STRENGTH_LABEL_KEYS[opt.strength])}`
                  : t('acctKeepDerived')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          className="bg-[#00C2C2] hover:bg-[#00A3A3] text-white"
          disabled={saving || clearing}
          onClick={onSave}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : t('save')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={saving || clearing}
          onClick={onCancel}
        >
          {t('cancel')}
        </Button>
        {hasOverride ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            disabled={saving || clearing}
            onClick={onClearOverride}
          >
            {clearing ? <Loader2 className="w-3 h-3 animate-spin" /> : t('acctClearOverride')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function SalesMapRow({
  row,
  onGetAdvice,
  isSendingAdvice,
  onRowUpdated,
  onClearOverride,
  style,
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [rationale, setRationale] = useState('');
  const [scoreChoice, setScoreChoice] = useState('keep');
  const [validationError, setValidationError] = useState('');

  const openEditor = useCallback(() => {
    const ro = row.rep_override;
    setRationale(ro?.rationale || '');
    setScoreChoice(
      ro?.has_score_override && row.score != null ? String(row.score) : 'keep',
    );
    setValidationError('');
    setEditing(true);
  }, [row]);

  const closeEditor = useCallback(() => {
    setEditing(false);
    setValidationError('');
  }, []);

  const handleSave = useCallback(async () => {
    const trimmed = rationale.trim();
    if (!trimmed) {
      setValidationError(t('acctRationaleRequired'));
      return;
    }

    setValidationError('');
    setSaving(true);
    try {
      const patch = { rationale: trimmed };
      if (scoreChoice !== 'keep') {
        patch.override_score = Number(scoreChoice);
      }
      const savedRow = await onRowUpdated(row.sales_point, patch);
      setEditing(false);
      toast({ title: t('acctSalesMapUpdated') });
      return savedRow;
    } catch (err) {
      toast({
        title: t('acctCouldNotSaveEdit'),
        description: err?.message || t('pleaseTryAgain'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }, [rationale, scoreChoice, row.sales_point, onRowUpdated, toast, t]);

  const handleClearOverride = useCallback(async () => {
    setClearing(true);
    try {
      await onClearOverride(row.sales_point);
      setEditing(false);
      toast({ title: t('acctOverrideCleared') });
    } catch (err) {
      toast({
        title: t('acctCouldNotClearOverride'),
        description: err?.message || t('pleaseTryAgain'),
        variant: 'destructive',
      });
    } finally {
      setClearing(false);
    }
  }, [onClearOverride, row.sales_point, toast, t]);

  return (
    <div
      className="border border-gray-100 rounded-lg p-4 bg-white shadow-sm animate-in fade-in duration-300"
      style={style}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-[#0A1A2F]">{salesPointDisplayLabel(row, t)}</p>
            {row.sales_point === 'current_vendor' && isCompetitorAutoOverride(row.rep_override) ? (
              <AutoDetectedPill />
            ) : null}
          </div>
          <MeddpiccSubtitle salesPoint={row.sales_point} />
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-[#5C6B7A] hover:text-[#0A1A2F]"
            title={t('edit')}
            disabled={saving || clearing}
            onClick={openEditor}
          >
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-xs"
            disabled={isSendingAdvice || saving || clearing}
            onClick={() => onGetAdvice(row)}
          >
            {t('acctGetAdvice')}
          </Button>
        </div>
      </div>

      <div className="flex items-start gap-2 mt-2">
        <div className="pt-0.5 shrink-0 flex flex-col gap-1.5">
          <StrengthBadge label={resolveStrengthLabel(row)} />
          {row.rep_override && !isCompetitorAutoOverride(row.rep_override) ? <EditedBadge /> : null}
        </div>
        <div className="flex-1 min-w-0">
          <TruncatedView text={row.current_view} />
        </div>
      </div>

      <SourceRefs refs={row.source_signal_refs} row={row} />

      {editing ? (
        <SalesMapRowEditor
          row={row}
          rationale={rationale}
          scoreChoice={scoreChoice}
          validationError={validationError}
          saving={saving}
          clearing={clearing}
          onRationaleChange={setRationale}
          onScoreChange={setScoreChoice}
          onSave={handleSave}
          onCancel={closeEditor}
          onClearOverride={handleClearOverride}
        />
      ) : row.rep_override ? (
        <RepOverrideNote
          repOverride={row.rep_override}
          onEdit={openEditor}
          onClear={handleClearOverride}
          clearing={clearing}
        />
      ) : null}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3 p-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="rounded-lg border border-gray-100 p-4 space-y-2 animate-in fade-in"
          style={{ animationDelay: `${i * 50}ms` }}
        >
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}

export default function SalesMapTab({
  accountId,
  accountName,
  tenantId,
  startChatSession,
}) {
  const { t, language } = useTranslation();
  const user = useUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [adviceLoadingPoint, setAdviceLoadingPoint] = useState(null);
  const refreshInFlightRef = useRef(false);

  const load = useCallback(
    async (force = false) => {
      if (!tenantId || !accountId) return;
      setError(null);

      // Stale-while-revalidate: paint cached map immediately, then refresh if needed.
      if (!force) {
        const cached = peekSalesMapCache(tenantId, accountId);
        if (cached) {
          setData(cached);
          setLoading(false);
          if (isSalesMapCacheFresh(tenantId, accountId)) return;
          try {
            const payload = await fetchAccountSalesMap(
              tenantId,
              accountId,
              user?.id,
              { force: false },
            );
            setData(payload);
          } catch (err) {
            // Keep showing stale cache; only surface error when we have nothing.
            if (!peekSalesMapCache(tenantId, accountId)) {
              setError(err?.message || 'Failed to load Sales Map');
            }
          }
          return;
        }
      }

      setLoading(true);
      try {
        const payload = await fetchAccountSalesMap(
          tenantId,
          accountId,
          user?.id,
          { force },
        );
        setData(payload);
      } catch (err) {
        setError(err?.message || 'Failed to load Sales Map');
      } finally {
        setLoading(false);
      }
    },
    [tenantId, accountId, user?.id],
  );

  const handleRefresh = useCallback(async () => {
    if (!tenantId || !accountId || refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    setRefreshing(true);
    setError(null);
    try {
      const payload = await fetchAccountSalesMap(
        tenantId,
        accountId,
        user?.id,
        { refresh: true },
      );
      setData(payload);
    } catch (err) {
      // Keep existing payload; surface error without blanking the panel.
      setError(err?.message || 'Failed to refresh Sales Map');
    } finally {
      refreshInFlightRef.current = false;
      setRefreshing(false);
    }
  }, [tenantId, accountId, user?.id]);

  useEffect(() => {
    load(false);
  }, [load]);

  const tableRows = useMemo(
    () => getSalesMapTableRows(data?.rows),
    [data?.rows],
  );
  const missingCount = useMemo(
    () => countMissingSalesMapRows(data?.rows),
    [data?.rows],
  );
  const showSparseBanner = tableRows.length > 0 && missingCount >= 6;

  const displayName = data?.account_name || accountName || 'Account';
  const country = data?.country ? ` (${data.country})` : '';
  const generatedLabel = formatGeneratedAt(data?.generated_at, DATE_FNS_LOCALES[language]);

  const trackAction = useCallback(
    (salesPoint, action) => {
      trackSalesMapAction({
        tenantId,
        userId: user?.id,
        accountId,
        salesPoint,
        action,
      }).catch(() => {});
    },
    [tenantId, user?.id, accountId],
  );

  const openChatSession = useCallback(
    (sessionContext) => {
      if (!startChatSession) return;
      startChatSession({
        ...sessionContext,
        chatbotRequestId: Date.now(),
        account_id: accountId,
        tenant_id: tenantId,
      });
    },
    [startChatSession, accountId, tenantId],
  );

  const handleGetAdvice = useCallback(
    (row) => {
      setAdviceLoadingPoint(row.sales_point);
      const question = `Get Advice on the '${row.label}' point of the Sales Map for ${displayName}.`;
      trackAction(row.sales_point, 'get_advice');
      openChatSession({
        autoSendChatbot: true,
        chatbotQuestion: question,
        chatbotContext: {
          sales_map_row: {
            sales_point: row.sales_point,
            label: row.label,
            current_view: row.current_view,
            score: row.score,
            strength_label: resolveStrengthLabel(row),
            gap: row.gap,
            recommended_next_step: row.recommended_next_step,
            source_signal_refs: row.source_signal_refs,
          },
        },
        preselectedAccount: {
          account_id: accountId,
          account_name: displayName,
        },
      });
      setTimeout(() => setAdviceLoadingPoint(null), 500);
    },
    [displayName, openChatSession, trackAction, accountId],
  );

  // [2026-10-12 perf] Lazy Next Best Move.
  //
  // fetchAccountSalesMap now sends include_nbm=0, which is what took the tab's
  // cold load from ~23s to the scorecard-only path. NBM is fetched here in the
  // background once the map has painted, then merged into `data` so
  // handleSuggestNextMove below finds it without the user waiting.
  //
  // Guarded by a ref keyed on tenant+account so a slow NBM response from a
  // previously-viewed account can never land on the account now on screen.
  const nbmRequestedRef = useRef(null);
  useEffect(() => {
    if (!tenantId || !accountId || !data) return;
    if (resolveNextBestMove(data)) return;            // already have one
    const key = `${tenantId}::${accountId}`;
    if (nbmRequestedRef.current === key) return;      // one attempt per account
    nbmRequestedRef.current = key;

    let cancelled = false;
    (async () => {
      try {
        const nbm = await fetchAccountSalesMapNbm(tenantId, accountId, user?.id);
        if (cancelled || !nbm) return;
        mergeNbmIntoSalesMapCache(tenantId, accountId, nbm);
        setData((prev) => (prev ? { ...prev, next_best_move: nbm } : prev));
      } catch {
        // Deliberately silent: the map is already on screen and nothing renders
        // NBM. Failing loudly here would blank a working panel for a value the
        // user has not asked for yet.
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, accountId, data, user?.id]);

  const handleSuggestNextMove = useCallback(() => {
    trackAction('recommended_next_move', 'suggest_next_move');

    const nbm = sanitizeNextBestMoveForDisplay(resolveNextBestMove(data));

    openChatSession({
      showNextBestMove: true,
      chatbotContext: {
        sales_map_account_id: accountId,
        ...(nbm ? { next_best_move: nbm } : {}),
      },
      salesMapPayload: data,
    });
  }, [openChatSession, accountId, trackAction, data]);

  const handlePrepareMeeting = useCallback(() => {
    trackAction('all', 'prepare_meeting');
    openChatSession({
      triggerMeetingPrep: true,
      prefilledPrompt: 'Prepare me for a meeting',
      preselectedAccount: {
        account_id: accountId,
        account_name: displayName,
        id: accountId,
      },
      salesMapPayload: data,
    });
  }, [trackAction, accountId, displayName, data, openChatSession]);

  const handleRowUpdated = useCallback(
    async (salesPoint, patch) => {
      const savedRow = await patchSalesMapOverride(
        accountId,
        salesPoint,
        tenantId,
        user?.id,
        patch,
      );
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: (prev.rows || []).map((r) =>
            r.sales_point === savedRow.sales_point ? savedRow : r,
          ),
        };
      });
      return savedRow;
    },
    [accountId, tenantId, user?.id],
  );

  const handleClearOverride = useCallback(
    async (salesPoint) => {
      await deleteSalesMapOverride(accountId, salesPoint, tenantId, user?.id);
      const payload = await fetchAccountSalesMap(
        tenantId,
        accountId,
        user?.id,
        { force: true },
      );
      setData(payload);
    },
    [accountId, tenantId, user?.id],
  );

  if (loading && !data) {
    return <LoadingSkeleton />;
  }

  if (error && !data) {
    return (
      <div className="p-6">
        <Alert variant="destructive" className="border-red-200 bg-red-50">
          <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <span className="text-red-800">{t('acctCouldNotLoadSalesMap')}</span>
            <Button size="sm" variant="outline" onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3 mr-1" />
              )}
              {t('retry')}
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="p-3 md:p-6 space-y-4">
        {error && data ? (
          <Alert variant="destructive" className="border-red-200 bg-red-50">
            <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <span className="text-red-800">
                {t('acctCouldNotRefreshSalesMap')}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3 mr-1" />
                )}
                {t('retry')}
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-[#0A1A2F]">
              {t('acctSalesMapTitle', { account: displayName })}
              {country}
            </h2>
            {generatedLabel ? (
              <p className="text-xs text-[#5C6B7A] mt-1 flex items-center gap-2">
                {t('acctGeneratedAt', { date: generatedLabel })}
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-[#00C2C2] hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                  onClick={handleRefresh}
                  disabled={refreshing || loading}
                  aria-busy={refreshing}
                >
                  {refreshing ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3 h-3" />
                  )}
                  {refreshing ? t('refreshing') : t('refresh')}
                </button>
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              className="bg-[#00C2C2] hover:bg-[#00A3A3] text-white"
              onClick={handlePrepareMeeting}
            >
              {t('acctPrepareForMeeting')}
            </Button>
            <Button
              className="bg-[#0A1A2F] hover:bg-[#0A1A2F]/90 text-white"
              onClick={handleSuggestNextMove}
            >
              {t('acctSuggestNextMove')}
            </Button>
          </div>
        </div>

        {showSparseBanner && (
          <Alert className="border-amber-200 bg-amber-50">
            <AlertDescription className="text-sm text-amber-900">
              {t('acctSalesMapSparseBanner')}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          {tableRows.map((row, index) => (
            <SalesMapRow
              key={row.sales_point}
              row={row}
              onGetAdvice={handleGetAdvice}
              isSendingAdvice={adviceLoadingPoint === row.sales_point}
              onRowUpdated={handleRowUpdated}
              onClearOverride={handleClearOverride}
              style={{ animationDelay: `${index * 40}ms` }}
            />
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
}
